import { Alert } from '@/components/DynamicUI/Alert';
import { AlertSeverity } from '@/constants/alertTypes';
import { Card, Icon, styled, Typography, Skeleton, Box } from '@mui/material';
import NumberFlow from '@number-flow/react';
import { ModalLink } from './formElements/typographies/ModalLink';
import { LinkTypes } from '@/constants/linkTypes';
import {
  TypographyAlign,
  TypographyColors,
  TypographyFontWeight,
  TypographyVariants,
} from '@/constants/typographyTypes';
import { Price } from './Price';
import { StyledChip as TermChip } from './formElements/selects/StyledChip';
import { PriceCard as PriceCardType } from '@/schemas/sceneDataSchema';
import { PricingTerm } from '@/constants/pricingTypes';
import { hasValidationErrors, useQuote, useShowLoader, useUpdatePricingTerm } from '@/hooks/use-quote';
import { ComponentsTypes } from '@/constants/componentsTypes';
import useAnalytics, { eventName } from '@/hooks/use-analytics';
import { ButtonSizes, ButtonTypes, ButtonColors, ButtonVariants } from '@/constants/buttonTypes';
import { DynamicRenderer } from './DynamicRenderer';
import { ModalDialog } from './ModalDialog';
import { CoverageSubmitButton } from './formElements/buttons/CoverageSubmitButton';
import { ModalButton } from './formElements/buttons/ModalButton';
import { RouteFn } from '@/hooks/use-flow';

// Smart Component - requires zustand quote cache
export const PriceCard = ({ component, route }: { component: PriceCardType; route: RouteFn }) => {
  const quote = useQuote();
  const showLoader = useShowLoader();
  const updatePricingTerm = useUpdatePricingTerm();
  const { trackEvent } = useAnalytics();
  const hasErrors = quote ? hasValidationErrors(quote) : false;

  if (!quote) {
    return (
      <StyledCard variant='outlined'>
        <Typography variant='body1'>Loading pricing...</Typography>
      </StyledCard>
    );
  }

  const {
    initial_term_selection,
    full_term_savings_in_cents,
    driver_count,
    vehicle_count,
    monthly_in_cents,
    full_term_in_cents,
    due_today_monthly_in_dollars,
    due_today_full_term_in_dollars,
    estimated_quote,
  } = quote.details;

  const priceInCents = initial_term_selection === PricingTerm.MONTHLY ? monthly_in_cents : full_term_in_cents;
  const isPriceZero = priceInCents === 0;

  const nudgeText = component.nudge_text ?? 'Scroll to customize your plan';

  const handleNudgeClick = () => {
    if (!component.nudge_scroll_target_id) return;
    trackEvent(`${eventName(nudgeText)}_SCROLL_NUDGE_CLICKED`);
    document.getElementById(component.nudge_scroll_target_id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const dueToday =
    initial_term_selection === PricingTerm.MONTHLY ? due_today_monthly_in_dollars : due_today_full_term_in_dollars;

  const savings = full_term_savings_in_cents / 100;
  const savingsLabel = Number.isInteger(savings) ? savings : savings.toFixed(2);

  const handleTermToggle = (term: PricingTerm) => {
    trackEvent(`${eventName(term)}_SELECTED`);
    void updatePricingTerm(term);
  };

  const checkIcon = <CheckIcon color='primary'>check</CheckIcon>;
  const driverText = `${driver_count} driver${driver_count > 1 ? 's' : ''}`;
  const vehicleText = `${vehicle_count} vehicle${vehicle_count > 1 ? 's' : ''}`;

  return (
    <>
      <Typography variant='h2'>{component.card_heading_text}</Typography>
      {component.show_term_selection && (
        <ChipContainer>
          <TermChip
            label='Monthly'
            icon={initial_term_selection === PricingTerm.MONTHLY ? checkIcon : undefined}
            selected={initial_term_selection === PricingTerm.MONTHLY}
            size='medium'
            variant='outlined'
            onClick={() => handleTermToggle(PricingTerm.MONTHLY)}
          />
          <TermChip
            label={`6 months (save $${savingsLabel})`}
            icon={initial_term_selection === PricingTerm.FULL_TERM ? checkIcon : undefined}
            selected={initial_term_selection === PricingTerm.FULL_TERM}
            size='medium'
            variant='outlined'
            onClick={() => handleTermToggle(PricingTerm.FULL_TERM)}
          />
        </ChipContainer>
      )}
      <StyledCard variant='outlined'>
        <DriverVehicleModal>
          {component.profile_modal_id && (
            <>
              <IdCard color='primary'>id_card</IdCard>
              <ModalLink
                component={{
                  type: LinkTypes.MODAL,
                  text: `${driverText}, ${vehicleText}`,
                  method_type: 'open',
                  modal_id: component.profile_modal_id,
                  color: TypographyColors.PRIMARY,
                  align: TypographyAlign.LEFT,
                  variant: TypographyVariants.BODY1,
                  font_weight: TypographyFontWeight.MEDIUM,
                }}
              />
            </>
          )}
        </DriverVehicleModal>

        <Price
          component={{
            type: ComponentsTypes.PRICE,
            variant: TypographyVariants.H1,
          }}
        />

        {component.price_estimate_modal_id && estimated_quote && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ModalLink
              component={{
                type: LinkTypes.MODAL,
                text: 'Price estimate',
                method_type: 'open',
                modal_id: component.price_estimate_modal_id,
                color: TypographyColors.TEXT_SECONDARY,
                align: TypographyAlign.LEFT,
                variant: TypographyVariants.BODY1,
                font_weight: TypographyFontWeight.REGULAR,
              }}
            />
            <Icon color='secondary'>help_outline</Icon>
          </Box>
        )}

        {!isPriceZero && (
          <DueTodayRow>
            {dueToday ? (
              <>
                {showLoader ? (
                  <Skeleton
                    variant='rounded'
                    width='100%'
                    height={27}
                    animation='wave'
                  />
                ) : (
                  <>
                    <Typography variant='h4'>
                      <NumberFlow
                        // CPAPI returns a negative value for refunds; Math.abs shows this without the "-", then we include the "Refund" suffix
                        value={Math.abs(dueToday ?? 0)}
                        format={{ style: 'currency', currency: 'USD' }}
                        suffix={dueToday < 0 ? ' Refund' : ' Due today'}
                        spinTiming={{ duration: 750, easing: 'ease-in-out' }}
                        opacityTiming={{ duration: 350, easing: 'ease-in-out' }}
                        willChange
                      />
                    </Typography>
                    {component.payment_details_modal_id && (
                      <ModalLink
                        component={{
                          type: LinkTypes.MODAL,
                          text: 'See details',
                          method_type: 'open',
                          modal_id: component.payment_details_modal_id,
                          color: TypographyColors.TEXT_PRIMARY,
                          align: TypographyAlign.LEFT,
                          variant: TypographyVariants.BODY2,
                          font_weight: TypographyFontWeight.REGULAR,
                        }}
                      />
                    )}
                  </>
                )}
              </>
            ) : (
              <Typography variant='h4'>Amount due today calculated at checkout</Typography>
            )}
          </DueTodayRow>
        )}
      </StyledCard>
      {hasErrors && (
        <>
          <ShowOnMobile>
            <StyledFixedAlert>
              <Alert
                severity={AlertSeverity.ERROR}
                title={null}
                titleGutterBottom={false}
                text={'There is a conflict with your selections. Please resolve before continuing.'}
              />
            </StyledFixedAlert>
          </ShowOnMobile>
          <ShowOnDesktop>
            <Alert
              severity={AlertSeverity.ERROR}
              title={'Invalid Coverage Selection'}
              titleGutterBottom={false}
              text={'There is a conflict with your selections. Please resolve before continuing.'}
            />
          </ShowOnDesktop>
        </>
      )}
      <ShowOnDesktop>
        <CoverageSubmitButton
          component={{
            id: component.form_id,
            form_id: component.form_id,
            label: component.submit_button_text,
            size: ButtonSizes.LARGE,
            color: ButtonColors.PRIMARY,
            start_icon: null,
            variant: ButtonVariants.CONTAINED,
            type: ButtonTypes.COVERAGE_SUBMIT,
            end_icon: null,
          }}
        />
      </ShowOnDesktop>
      {/* Child subtree (e.g. the one-product "Learn more" callout) renders below
          the desktop CTA and above the mobile scroll nudge. Because the CTA and
          nudge are breakpoint-exclusive, this single slot matches the Figma on
          both: desktop shows price → CTA → callout, mobile shows price → callout
          → nudge. */}
      {component.components && component.components.length > 0 && (
        <DynamicRenderer
          components={component.components}
          route={route}
        />
      )}
      <ShowOnMobile>
        <ScrollNudge
          isInteractive={!!component.nudge_scroll_target_id}
          onClick={component.nudge_scroll_target_id ? handleNudgeClick : undefined}
        >
          <Typography
            variant='body2'
            align='center'
            fontWeight={'medium'}
          >
            {nudgeText}
          </Typography>
          <Icon fontSize='small'>arrow_downward</Icon>
        </ScrollNudge>
      </ShowOnMobile>
      <ModalDialog
        children={
          <DynamicRenderer
            components={quote.payment_modal_content}
            route={async () => {}}
          />
        }
        id={component.payment_details_modal_id}
        status='closed'
        footerChildren={
          <ModalButton
            component={{
              id: `payment_details_modal_button_close`,
              label: 'Close',
              size: ButtonSizes.LARGE,
              color: ButtonColors.PRIMARY,
              start_icon: null,
              end_icon: null,
              variant: ButtonVariants.CONTAINED,
              type: ButtonTypes.MODAL,
              modal_id: component.payment_details_modal_id,
              method_type: 'close',
            }}
          />
        }
      />
    </>
  );
};

const ShowOnDesktop = styled('div')`
  display: none;
  @media (min-width: 768px) {
    display: block;
  }
`;

const ShowOnMobile = styled('div')`
  @media (min-width: 768px) {
    display: none;
  }
`;

const bounceAnimation = `
  @keyframes bounce {
    0%, 20%, 100% {
      transform: translateY(0);
    }
    10% {
      transform: translateY(5px);
    }
  }
`;

const ScrollNudge = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isInteractive',
})<{ isInteractive?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin-bottom: -8px;
  cursor: ${({ isInteractive }) => (isInteractive ? 'pointer' : 'default')};

  ${bounceAnimation}

  & > .MuiIcon-root {
    animation: bounce 3.5s ease-in-out infinite;
  }
`;

const StyledCard = styled(Card)`
  display: flex;
  padding: 16px;
  flex-direction: column;
  gap: 8px;
  border-radius: var(--border-radius-medium);
`;

const DriverVehicleModal = styled('div')`
  display: flex;
  align-items: flex-start;
  gap: var(--3, 8px);
  align-self: stretch;
`;

const IdCard = styled(Icon)`
  flex-shrink: 0;
  height: 24px;
  width: 24px;
  font-family: 'Material Symbols Outlined';
`;

const CheckIcon = styled(Icon)`
  & > .MuiIcon-root {
    height: 24px;
    width: 24px;
  }
`;

const ChipContainer = styled('div')`
  display: flex;
  align-items: center;
  align-content: center;
  gap: 8px var(--3, 8px);
  align-self: stretch;
  flex-wrap: wrap;
`;

const DueTodayRow = styled('div')`
  display: flex;
  align-items: center;
  gap: var(--3, 8px);
  align-self: stretch;
`;

const StyledFixedAlert = styled('div')`
  position: fixed;
  inset: auto 10px 80px;
  z-index: 1100;
`;
